"use client";

import { DISCORD_SUPPORT_URL } from "@cede/utils";
import { CedeLogo, DiscordIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useClipboard } from "@/hooks";
import { RefreshCw, Home, Copy, Check } from "lucide-react";

type ErrorPageContentProps = {
  error?: Error;
  supportId?: string;
  onReload?: () => void;
  onGoHome?: () => void;
  onReportDiscord?: () => void;
  showErrorDetails?: boolean;
  className?: string;
};

export const ErrorPageContent = ({
  error,
  supportId,
  onReload,
  onGoHome,
  onReportDiscord,
  showErrorDetails = true,
  className = "",
}: ErrorPageContentProps) => {
  const { isCopied, handleCopy } = useClipboard();
  const displaySupportId = supportId ?? error?.message ?? "Unavailable";

  const handleReload = () => {
    if (onReload) {
      onReload();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    } else {
      window.location.href = "/";
    }
  };

  const handleDiscordClick = () => {
    if (onReportDiscord) {
      onReportDiscord();
    } else {
      window.open(DISCORD_SUPPORT_URL, "_blank");
    }
  };

  const handleCopySupportId = async () => {
    if (!displaySupportId) return;
    await handleCopy(`${displaySupportId}`);
  };

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <div className="flex flex-col items-center gap-6">
        <div className="mx-auto p-4 flex h-24 w-24 items-center justify-center">
          <div className="drop-shadow-[0_0_12px_rgba(94,166,255,0.6)]">
            <CedeLogo width={96} height={96} />
          </div>
        </div>
        <h1 className="text-center text-2xl sm:text-3xl font-semibold text-cede-text-primary mb-2">
          Something went wrong
        </h1>
      </div>
      <p className="text-center text-sm text-cede-text-secondary mb-6">We encountered an unexpected error.</p>

      {showErrorDetails && displaySupportId && (
        <div className="w-full rounded-large border border-cede-error/30 bg-cede-error/10 p-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs font-semibold text-cede-error uppercase tracking-wide">Support ID</p>
              <code className="block break-all text-sm text-cede-error">{displaySupportId}</code>
            </div>
            <Tooltip open={isCopied}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCopySupportId}
                  className="h-6 w-6 text-cede-error hover:bg-cede-error/20 flex-shrink-0"
                  aria-label="Copy support id"
                >
                  {isCopied ? (
                    <Check className="h-3.5 w-3.5 animate-in fade-in-0 zoom-in-50 duration-200" />
                  ) : (
                    <Copy className="h-3.5 w-3.5 animate-in fade-in-0 zoom-in-50 duration-200" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Copied!</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      )}

      <p className="text-center my-2 text-xs text-cede-text-secondary">
        Please include this Support ID when reporting the issue on Discord.
      </p>

      <div className="flex flex-col gap-3 w-full">
        <Button
          variant="primary"
          size="large"
          leftIcon={<DiscordIcon className="h-5 w-5" />}
          onClick={handleDiscordClick}
          className="w-full"
        >
          Report on Discord
        </Button>
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="mr-2 h-4 w-4" />}
            onClick={handleReload}
            className="w-full"
          >
            Reload
          </Button>
          <Button
            variant="secondary"
            leftIcon={<Home className="mr-2 h-4 w-4" />}
            onClick={handleGoHome}
            className="w-full"
          >
            Go home
          </Button>
        </div>
      </div>
    </div>
  );
};
